/* ------------------------ Functional imports start ------------------------ */
import { useQuery, useQueries, type UseQueryResult } from '@tanstack/react-query'
import { type PaginatedResponse, type DetailResponse } from '../../../api/client'
import {
  fetchRequest,
  fetchRequests,
  fetchOffer,
  fetchOffers,
  fetchRequestFiles,
  searchRequests,
  searchOffers,
  type RequestFilters,
  type OfferFilters,
} from '../api/requestApi'
import { type Request, type Offer, type RequestFile } from '../utils/types'
import { type PaginatedQueryParameters, type DetailQueryParameters } from '../../../api/hooks/utils/types'
import { useSmartListQuery, useSmartDetailQuery } from '../../../api/hooks/useSmartQueries'
import { requestKeys, offerKeys } from '../utils/queryKeys'
/* ------------------------- Functional imports end ------------------------- */

/**
 * Hook that provides the available Request instances that are matching the
 * specified query parameters.
 * @param parameters - The parameters that define which instances to fetch from the backend.
 * @param instances - Array of instance ids or urls to fetch from the backend.
 * @param enabled - Disabling the query will prevent it from running instantly
 *  after the parameters or instances changed.
 */
export function useRequests(
  parameters: PaginatedQueryParameters<Request, RequestFilters>,
  instances: number[] | string[] | undefined = undefined,
  enabled = true,
): UseQueryResult<PaginatedResponse<Request>> {
  return useSmartListQuery<Request, RequestFilters>(
    {
      queryKeyFactory: requestKeys,
      queryParameters: parameters,
      queryFn: (_, ids: number[] | undefined) => fetchRequests(parameters, ids),
      elasticSearchFn: () => searchRequests(parameters)
    },
    enabled,
    instances,
  )
}

/**
 * Hook to fetch a single request by ID or URL
 */
export function useRequest(
  idOrUrl: number | string,
  parameters: DetailQueryParameters<Request>,
  enabled = true,
): UseQueryResult<DetailResponse<Request>> {
  return useSmartDetailQuery(
    idOrUrl,
    {
      queryKeyFactory: requestKeys,
      queryParameters: parameters,
      queryFn: (_, id) => fetchRequest(id, parameters.expand),
    },
    enabled
  )
}

/**
 * Hook that provides the available Offer instances that are matching the
 * specified query parameters.
 * @param parameters - The parameters that define which instances to fetch from the backend.
 * @param instances - Array of instance ids or urls to fetch from the backend.
 * @param enabled - Disabling the query will prevent it from running instantly
 *  after the parameters or instances changed.
 */
export function useOffers(
  parameters: PaginatedQueryParameters<Offer, OfferFilters>,
  instances: number[] | string[] | undefined = undefined,
  enabled = true,
): UseQueryResult<PaginatedResponse<Offer>> {
  return useSmartListQuery<Offer, OfferFilters>(
    {
      queryKeyFactory: offerKeys,
      queryParameters: parameters,
      queryFn: (_, ids: number[] | undefined) => fetchOffers(parameters, ids),
      elasticSearchFn: () => searchOffers(parameters)
    },
    enabled,
    instances,
  )
}

/**
 * Hook to fetch a single offer by ID or URL
 */
export function useOffer(
  idOrUrl: number | string,
  parameters: DetailQueryParameters<Offer>,
  enabled = true,
): UseQueryResult<DetailResponse<Offer>> {
  return useSmartDetailQuery(
    idOrUrl,
    {
      queryKeyFactory: offerKeys,
      queryParameters: parameters,
      queryFn: (_, id) => fetchOffer(id, parameters.expand),
    },
    enabled
  )
}

/**
 * Hook to fetch request files
 */
export function useRequestFiles(
  requestIdOrSelf: string | number | undefined,
  enabled = true
): UseQueryResult<RequestFile[], Error> {
  return useQuery({
    queryKey: [...requestKeys.all, 'files', String(requestIdOrSelf)],
    queryFn: () => {
      if (!requestIdOrSelf) {
        throw new Error('Request ID or URL is required')
      }
      return fetchRequestFiles(requestIdOrSelf)
    },
    enabled: enabled && requestIdOrSelf !== undefined,
  })
}

/**
 * Hook to fetch multiple requests from Elasticsearch search results
 * Loads full request data from the database for each request ID
 * 
 * @param requestIds - Array of request IDs from Elasticsearch search
 * @param expand - Fields to expand in the request response
 * @param enabled - Whether to enable the queries
 * @returns Object with requests, loading states, and errors
 * 
 * @example
 * ```tsx
 * const { requests, isLoading, hasErrors } = useRequestsFromSearch(
 *   ['1', '2', '3'],
 *   ['editors', 'files', 'article', 'article__files', 'article__properties', 'article__supplier', 'contact']
 * )
 * ```
 */
export function useRequestsFromSearch(
  requestIds: string[],
  expand?: string[],
  enabled = true
) {
  const queries = useQueries({
    queries: requestIds.map((id) => ({
      queryKey: requestKeys.detail(id, { expand }),
      queryFn: () => fetchRequest(id, expand),
      enabled: enabled && !!id,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    })),
  })

  // Extract requests from query results
  const requests = queries
    .filter((query) => query.isSuccess && query.data)
    .map((query) => query.data as Request)

  // Check if any queries are still loading
  const isLoading = queries.some((query) => query.isLoading)

  // Check if all queries are done (either success or error)
  const isIdle = queries.every((query) => query.status === 'pending')
  const isDone = queries.every((query) => query.status !== 'pending')

  // Check for errors
  const hasErrors = queries.some((query) => query.isError)
  const errors = queries
    .filter((query) => query.isError)
    .map((query) => query.error)

  // Calculate progress
  const totalQueries = requestIds.length
  const completedQueries = queries.filter(
    (query) => query.status !== 'pending'
  ).length
  const progress = totalQueries > 0 ? (completedQueries / totalQueries) * 100 : 0

  return {
    requests,
    isLoading,
    isIdle,
    isDone,
    hasErrors,
    errors,
    progress,
    queries, // Expose individual queries for advanced use cases
  }
}

/**
 * Hook to fetch multiple offers from Elasticsearch search results
 * Loads full offer data from the database for each offer ID
 * 
 * @param offerIds - Array of offer IDs from Elasticsearch search
 * @param expand - Fields to expand in the offer response
 * @param enabled - Whether to enable the queries
 * @returns Object with offers, loading states, and errors
 * 
 * @example
 * ```tsx
 * const { offers, isLoading, hasErrors } = useOffersFromSearch(
 *   ['1', '2', '3'],
 *   ['editors', 'request', 'files']
 * )
 * ```
 */
export function useOffersFromSearch(
  offerIds: string[],
  expand?: string[],
  enabled = true
) {
  const queries = useQueries({
    queries: offerIds.map((id) => ({
      queryKey: offerKeys.detail(id, { expand }),
      queryFn: () => fetchOffer(id, expand),
      enabled: enabled && !!id,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    })),
  })

  // Extract offers from query results
  const offers = queries
    .filter((query) => query.isSuccess && query.data)
    .map((query) => query.data as Offer)

  // Check if any queries are still loading
  const isLoading = queries.some((query) => query.isLoading)

  // Check if all queries are done (either success or error)
  const isIdle = queries.every((query) => query.status === 'pending')
  const isDone = queries.every((query) => query.status !== 'pending')

  // Check for errors
  const hasErrors = queries.some((query) => query.isError)
  const errors = queries
    .filter((query) => query.isError)
    .map((query) => query.error)

  // Calculate progress
  const totalQueries = offerIds.length
  const completedQueries = queries.filter(
    (query) => query.status !== 'pending'
  ).length
  const progress = totalQueries > 0 ? (completedQueries / totalQueries) * 100 : 0

  return {
    offers,
    isLoading,
    isIdle,
    isDone,
    hasErrors,
    errors,
    progress,
    queries, // Expose individual queries for advanced use cases
  }
}
